import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box, Paper, Typography, Tabs, Tab, TextField, Button,
  Grid, Divider, Alert, Card, CardContent, CardActionArea,
  Avatar, CircularProgress, Chip, IconButton, Table,
  TableBody, TableCell, TableContainer, TableHead, TableRow,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Tooltip, Switch, FormControlLabel, useMediaQuery, useTheme
} from '@mui/material';
import {
  Save as SaveIcon,
  Refresh as RefreshIcon,
  Palette as PaletteIcon,
  Store as StoreIcon,
  Search as SeoIcon,
  LocalShipping as ShippingIcon,
  Check as CheckIcon,
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
  Language as DomainIcon,
  VerifiedUser as VerifiedIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Add as AddIcon,
  ContentCopy as CopyIcon,
  DnsOutlined as DnsIcon,
  Payment as PaymentIcon,
  Campaign as MarketingIcon,
  Login as LoginIcon,
  Receipt as ReceiptIcon,
  Notifications as NotificationsIcon,
  ContactPhone as ContactIcon,
  AutoAwesome as FeaturesIcon,
  ArrowUpward as UpIcon,
  ArrowDownward as DownIcon,
  LocalOffer as LocalOfferIcon,
  VerifiedUser as VerifiedUserIcon,
  Support as SupportIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../../services/api';

// Storefront templates shown in admin.
const STORE_TEMPLATES = [
  { id: 'classic', nameKey: 'tenantSettings.templateClassic', descKey: 'tenantSettings.templateClassicDesc', image: '/images/landing/template-fashion.webp', color: '#1976d2' },
  { id: 'fashion', nameKey: 'tenantSettings.templateFashion', descKey: 'tenantSettings.templateFashionDesc', image: '/images/landing/template-fashion.webp', color: '#1a1a1a' },
  { id: 'handmade', nameKey: 'tenantSettings.templateHandmade', descKey: 'tenantSettings.templateHandmadeDesc', image: '/images/landing/template-handmade.webp', color: '#8b6f47' },
  { id: 'food', nameKey: 'tenantSettings.templateFood', descKey: 'tenantSettings.templateFoodDesc', image: '/images/landing/template-food.webp', color: '#d4503a' },
  { id: 'tech', nameKey: 'tenantSettings.templateTech', descKey: 'tenantSettings.templateTechDesc', image: '/images/landing/template-tech.webp', color: '#0d1117' },
];

// Allowed icons for storefront feature cards.
const iconMap = {
  LocalOffer: LocalOfferIcon,
  LocalShipping: ShippingIcon,
  VerifiedUser: VerifiedUserIcon,
  Support: SupportIcon
};

// Tabs - labels resolved via i18n inside component.
const TAB_ICONS = [
  <StoreIcon />, <PaletteIcon />, <ShippingIcon />, <ContactIcon />,
  <FeaturesIcon />, <PaymentIcon />, <MarketingIcon />, <LoginIcon />,
  <SeoIcon />, <DomainIcon />, <ReceiptIcon />, <NotificationsIcon />,
];
const TAB_KEYS = [
  'tenantSettings.tabStore', 'tenantSettings.tabTheme', 'tenantSettings.tabShipping',
  'tenantSettings.tabContact', 'tenantSettings.tabFeatures', 'tenantSettings.tabPayment',
  'tenantSettings.tabMarketing', 'tenantSettings.tabAuth', 'tenantSettings.tabSeo',
  'tenantSettings.tabDomains', 'tenantSettings.tabEinvoice', 'tenantSettings.tabNotifications',
];

const TenantSettings = () => {
  const { t } = useTranslation('admin');
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [themes, setThemes] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('classic');

  // Theme
  const [themeConfig, setThemeConfig] = useState({
    theme_id: 'warm',
    primary_color: '#e65100'
  });

  // Store
  const [storeConfig, setStoreConfig] = useState({
    store_name: '',
    store_name_short: '',
    description: '',
    slogan: '',
    logo_url: '',
    favicon_url: ''
  });

  // Contact
  const [contactConfig, setContactConfig] = useState({
    contact_phone: '',
    contact_email: '',
    contact_address: '',
    line_id: '',
    service_hours: '',
    facebook_url: '',
    instagram_url: '',
    line_url: ''
  });

  // SEO
  const [seoConfig, setSeoConfig] = useState({
    site_title: '',
    meta_description: '',
    meta_keywords: '',
    google_verification: '',
    bing_verification: '',
    ai_seo_enabled: true,
    block_ai_training: true,
    llms_txt_custom: ''
  });

  // Shipping
  const [shippingConfig, setShippingConfig] = useState({
    base_fee: 60,
    free_shipping_threshold: 799
  });

  // Domains
  const [domains, setDomains] = useState([]);
  const [domainsLoading, setDomainsLoading] = useState(false);
  const [addDomainOpen, setAddDomainOpen] = useState(false);
  const [newDomain, setNewDomain] = useState('');
  const [addingDomain, setAddingDomain] = useState(false);
  const [verifyingId, setVerifyingId] = useState(null);

  // Payments
  const [paymentMethods, setPaymentMethods] = useState([]);

  // SmilePay
  const [smilepayConfig, setSmilepayConfig] = useState({
    dcvc: '', rvg2c_key: '', rot_url: '', pay_end_url: '', test_mode: true
  });

  // NCCC
  const [ncccConfig, setNcccConfig] = useState({
    merchant_id: '', terminal_id: '', hash_key: '', hash_iv: '', test_mode: true
  });

  // LINE Pay
  const [linepayConfig, setLinepayConfig] = useState({
    channel_id: '', channel_secret: '', test_mode: true
  });

  // Bank account (manual ATM)
  const [bankConfig, setBankConfig] = useState({
    bank_name: '', bank_code: '', bank_account: '', bank_holder: ''
  });

  // Marketing tracking
  const [marketingConfig, setMarketingConfig] = useState({
    gtm_id: '', ga4_id: '', google_ads_id: '', google_ads_label: '',
    fb_pixel_id: '', fb_access_token: '',
    tiktok_pixel_id: '', tiktok_access_token: '', tiktok_test_mode: false,
    google_search_console: '', merchant_center_id: '', merchant_feed_enabled: false
  });

  // Auth
  const [authConfig, setAuthConfig] = useState({
    phone_enabled: true,
    google_enabled: false, google_client_id: '', google_client_secret: '',
    line_enabled: false, line_channel_id: '', line_channel_secret: '', line_callback_url: ''
  });

  // Homepage features
  const [featuresConfig, setFeaturesConfig] = useState({
    feature1_icon: 'LocalOffer', feature1_title: '', feature1_desc: '',
    feature2_icon: 'LocalShipping', feature2_title: '', feature2_desc: '',
    feature3_icon: 'VerifiedUser', feature3_title: '', feature3_desc: '',
    feature4_icon: 'Support', feature4_title: '', feature4_desc: ''
  });

  // E-invoice
  const [einvoiceConfig, setEinvoiceConfig] = useState({
    grvc: '', verify_key: '', intype: '07', tax_type: '1',
    test_mode: false, print_enabled: false, auto_issue: false, default_carrier: 'EJ0113'
  });

  // Notifications
  const [notificationConfig, setNotificationConfig] = useState({
    sms: { username: '', password: '' },
    email: { provider: 'ses', from_email: '', from_name: '', smtp_host: '', smtp_port: '', smtp_user: '', smtp_pass: '' },
    line: { notify_token: '' }
  });

  useEffect(() => {
    loadAllSettings();
  }, []);

  const loadAllSettings = async () => {
    setLoading(true);
    try {
      const results = await Promise.allSettled([
        api.get('/tenant-settings/themes'),       // 0
        api.get('/tenant-settings'),               // 1
        api.get('/settings/payment-methods'),       // 2
        api.get('/settings/smilepay'),              // 3
        api.get('/settings/nccc'),                  // 4
        api.get('/settings/linepay'),               // 5
        api.get('/settings/marketing'),             // 6
        api.get('/settings/auth'),                  // 7
        api.get('/settings/features'),              // 8
        api.get('/settings/einvoice'),              // 9
        api.get('/settings/notifications'),         // 10
        api.get('/tenant-settings/payment'),        // 11
      ]);

      // Themes
      if (results[0].status === 'fulfilled' && results[0].value.data.success) {
        setThemes(results[0].value.data.themes);
      }

      // Tenant settings
      if (results[1].status === 'fulfilled' && results[1].value.data.success) {
        const config = results[1].value.data.config || {};
        setThemeConfig({ theme_id: config.theme_id || 'warm', primary_color: config.primary_color || '#e65100' });
        setStoreConfig({
          store_name: config.store_name || '', store_name_short: config.store_name_short || '',
          description: config.description || '', slogan: config.slogan || '',
          logo_url: config.logo_url || '', favicon_url: config.favicon_url || ''
        });
        setContactConfig({
          contact_phone: config.contact_phone || '', contact_email: config.contact_email || '',
          contact_address: config.contact_address || '', line_id: config.line_id || '',
          service_hours: config.service_hours || '', facebook_url: config.facebook_url || '',
          instagram_url: config.instagram_url || '', line_url: config.line_url || ''
        });
        setSeoConfig({
          site_title: config.site_title || '', meta_description: config.meta_description || '',
          meta_keywords: config.meta_keywords || '', google_verification: config.google_verification || '',
          bing_verification: config.bing_verification || '',
          ai_seo_enabled: config.ai_seo_enabled !== '0',
          block_ai_training: config.block_ai_training !== '0',
          llms_txt_custom: config.llms_txt_custom || ''
        });
        setShippingConfig({
          base_fee: parseInt(config.base_fee) || 60,
          free_shipping_threshold: parseInt(config.free_shipping_threshold) || 799
        });
        setSelectedTemplate(config.template_id || 'classic');
      }

      // Payment methods
      if (results[2].status === 'fulfilled' && results[2].value.data.success) {
        setPaymentMethods(results[2].value.data.payment_methods);
      }

      // SmilePay
      if (results[3].status === 'fulfilled' && results[3].value.data.success) {
        const c = results[3].value.data.config;
        setSmilepayConfig(prev => ({ ...prev, ...c, test_mode: c.test_mode === '1' }));
      }

      // NCCC
      if (results[4].status === 'fulfilled' && results[4].value.data.success) {
        const c = results[4].value.data.config;
        setNcccConfig(prev => ({ ...prev, ...c, test_mode: c.test_mode === '1' }));
      }

      // LINE Pay
      if (results[5].status === 'fulfilled' && results[5].value.data.success) {
        const c = results[5].value.data.config;
        setLinepayConfig(prev => ({ ...prev, ...c, test_mode: c.test_mode === '1' }));
      }

      // Marketing
      if (results[6].status === 'fulfilled' && results[6].value.data.success) {
        const c = results[6].value.data.config;
        setMarketingConfig(prev => ({ ...prev, ...c, merchant_feed_enabled: c.merchant_feed_enabled === '1', tiktok_test_mode: c.tiktok_test_mode === '1' }));
      }

      // Auth
      if (results[7].status === 'fulfilled' && results[7].value.data.success) {
        const c = results[7].value.data.config;
        setAuthConfig(prev => ({
          ...prev, ...c,
          phone_enabled: c.phone_enabled === '1' || c.phone_enabled === true,
          google_enabled: c.google_enabled === '1' || c.google_enabled === true,
          line_enabled: c.line_enabled === '1' || c.line_enabled === true
        }));
      }

      // Features
      if (results[8].status === 'fulfilled' && results[8].value.data.success) {
        setFeaturesConfig(prev => ({ ...prev, ...results[8].value.data.config }));
      }

      // E-invoice
      if (results[9].status === 'fulfilled' && results[9].value.data.success) {
        const c = results[9].value.data.config;
        setEinvoiceConfig(prev => ({ ...prev, ...c, test_mode: c.test_mode === '1', print_enabled: c.print_enabled === '1', auto_issue: c.auto_issue === '1' }));
      }

      // Notifications
      if (results[10].status === 'fulfilled' && results[10].value.data.success) {
        const s = results[10].value.data.settings;
        if (s) setNotificationConfig(prev => ({ ...prev, ...s }));
      }

      // Bank account (tenant payment settings)
      if (results[11]?.status === 'fulfilled' && results[11].value.data.success) {
        const c = results[11].value.data.config;
        setBankConfig({
          bank_name: c.bank_name || '', bank_code: c.bank_code || '',
          bank_account: c.bank_account || '', bank_holder: c.bank_holder || ''
        });
      }
    } catch (error) {
      console.error('Load settings failed:', error);
      toast.error(t('tenantSettings.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  // Theme / template
  const handleSelectTheme = async (themeId) => {
    setSaving(true);
    try {
      const response = await api.put('/tenant-settings/theme', { theme_id: themeId });
      if (response.data.success) {
        setThemeConfig({ theme_id: themeId, primary_color: response.data.theme?.color || '#e65100' });
        toast.success('Theme updated.');
      }
    } catch (error) {
      toast.error(t('tenantSettings.updateThemeFailed'));
    } finally { setSaving(false); }
  };

  const handleSelectTemplate = async (templateId) => {
    setSaving(true);
    try {
      const response = await api.put('/tenant-settings', { template_id: templateId });
      if (response.data.success) {
        setSelectedTemplate(templateId);
        toast.success(t('tenantSettings.templateUpdated'));
      }
    } catch (error) {
      toast.error(t('tenantSettings.updateTemplateFailed'));
    } finally { setSaving(false); }
  };

  // Store info
  const handleSaveStore = async () => {
    setSaving(true);
    try {
      const response = await api.put('/tenant-settings', storeConfig);
      if (response.data.success) toast.success(t('tenantSettings.storeSaved'));
    } catch (error) { toast.error(t('tenantSettings.saveFailed')); }
    finally { setSaving(false); }
  };

  // Contact
  const handleSaveContact = async () => {
    setSaving(true);
    try {
      const response = await api.put('/tenant-settings', contactConfig);
      if (response.data.success) toast.success(t('tenantSettings.contactSaved'));
    } catch (error) { toast.error(t('tenantSettings.saveFailed')); }
    finally { setSaving(false); }
  };

  // ===== SEO =====
  const handleSaveSeo = async () => {
    setSaving(true);
    try {
      const response = await api.put('/tenant-settings', seoConfig);
      if (response.data.success) toast.success(t('tenantSettings.seoSaved'));
    } catch (error) { toast.error(t('tenantSettings.saveFailed')); }
    finally { setSaving(false); }
  };

  // Shipping
  const handleSaveShipping = async () => {
    setSaving(true);
    try {
      const response = await api.put('/tenant-settings', shippingConfig);
      if (response.data.success) toast.success(t('tenantSettings.shippingSaved'));
    } catch (error) { toast.error(t('tenantSettings.saveFailed')); }
    finally { setSaving(false); }
  };

  // Upload logo/favicon
  const handleImageUpload = async (event, field) => {
    const file = event.target.files[0];
    if (!file) return;
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];
    if (!allowedTypes.includes(file.type)) { toast.error(t('tenantSettings.unsupportedFormat')); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error(t('tenantSettings.fileSizeLimit')); return; }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('image', file);
      const response = await api.post('/upload/store', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (response.data.success) {
        setStoreConfig(prev => ({ ...prev, [field]: response.data.data.url }));
        toast.success(t('tenantSettings.uploadSuccess'));
      }
    } catch (error) { toast.error(t('tenantSettings.uploadFailed')); }
    finally { setUploading(false); }
  };

  const handleClearImage = (field) => {
    setStoreConfig(prev => ({ ...prev, [field]: '' }));
  };

  // Domains
  const loadDomains = async () => {
    setDomainsLoading(true);
    try {
      const res = await api.get('/tenant-settings/domains');
      if (res.data.success) setDomains(res.data.domains || []);
    } catch (error) { console.error('Load domains failed:', error); }
    finally { setDomainsLoading(false); }
  };

  const handleAddDomain = async () => {
    if (!newDomain.trim()) return;
    setAddingDomain(true);
    try {
      const res = await api.post('/tenant-settings/domains', { domain: newDomain.trim() });
      if (res.data.success) { toast.success(t('tenantSettings.domainAdded')); setAddDomainOpen(false); setNewDomain(''); loadDomains(); }
    } catch (error) { toast.error(error.response?.data?.error || t('tenantSettings.domainAddFailed')); }
    finally { setAddingDomain(false); }
  };

  const handleVerifyDomain = async (domainId) => {
    setVerifyingId(domainId);
    try {
      const res = await api.post(`/tenant-settings/domains/${domainId}/verify`);
      if (res.data.success) {
        if (res.data.verified) toast.success(t('tenantSettings.dnsVerified'));
        else toast.warning(res.data.message || t('tenantSettings.dnsNotVerified'));
        loadDomains();
      }
    } catch (error) { toast.error(error.response?.data?.error || t('tenantSettings.dnsVerifyFailed')); }
    finally { setVerifyingId(null); }
  };

  const handleSetPrimary = async (domainId) => {
    try {
      const res = await api.put(`/tenant-settings/domains/${domainId}/primary`);
      if (res.data.success) { toast.success(t('tenantSettings.primaryDomainUpdated')); loadDomains(); }
    } catch (error) { toast.error(error.response?.data?.error || t('tenantSettings.saveFailed')); }
  };

  const handleDeleteDomain = async (domainId, domainName) => {
    if (!window.confirm(t('tenantSettings.confirmDeleteDomain', { domain: domainName }))) return;
    try {
      const res = await api.delete(`/tenant-settings/domains/${domainId}`);
      if (res.data.success) { toast.success(t('tenantSettings.domainDeleted')); loadDomains(); }
    } catch (error) { toast.error(error.response?.data?.error || t('tenantSettings.deleteFailed')); }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => toast.info(t('tenantSettings.copied')));
  };

  // Payments
  const togglePaymentMethod = async (id, isActive) => {
    try {
      await api.put(`/settings/payment-methods/${id}`, { is_active: isActive ? 1 : 0 });
      setPaymentMethods(prev => prev.map(m => m.id === id ? { ...m, is_active: isActive ? 1 : 0 } : m));
      toast.success(t('tenantSettings.saved'));
    } catch (error) { toast.error(t('tenantSettings.saveFailed')); }
  };

  const movePaymentMethod = async (id, direction) => {
    const index = paymentMethods.findIndex(m => m.id === id);
    if ((direction === -1 && index === 0) || (direction === 1 && index === paymentMethods.length - 1)) return;
    const newMethods = [...paymentMethods];
    const temp = newMethods[index];
    newMethods[index] = newMethods[index + direction];
    newMethods[index + direction] = temp;
    const items = newMethods.map((m, i) => ({ id: m.id, sort_order: i + 1 }));
    try {
      await api.put('/settings/payment-methods/batch/sort', { items });
      setPaymentMethods(newMethods.map((m, i) => ({ ...m, sort_order: i + 1 })));
      toast.success(t('tenantSettings.saved'));
    } catch (error) { toast.error(t('tenantSettings.sortUpdateFailed')); }
  };

  const saveBankConfig = async () => {
    setSaving(true);
    try {
      await api.put('/tenant-settings', bankConfig);
      toast.success(t('tenantSettings.saved'));
    } catch (error) { toast.error(t('tenantSettings.saveFailed')); } finally { setSaving(false); }
  };

  const saveSmilepayConfig = async () => {
    setSaving(true);
    try { await api.put('/settings/smilepay', smilepayConfig); toast.success(t('tenantSettings.smilepaySaved')); }
    catch (error) { toast.error(t('tenantSettings.saveFailed')); } finally { setSaving(false); }
  };

  const saveNcccConfig = async () => {
    setSaving(true);
    try { await api.put('/settings/nccc', ncccConfig); toast.success(t('tenantSettings.ncccSaved')); }
    catch (error) { toast.error(t('tenantSettings.saveFailed')); } finally { setSaving(false); }
  };

  const saveLinepayConfig = async () => {
    setSaving(true);
    try { await api.put('/settings/linepay', linepayConfig); toast.success(t('tenantSettings.linepaySaved')); }
    catch (error) { toast.error(t('tenantSettings.saveFailed')); } finally { setSaving(false); }
  };

  const saveMarketingConfig = async () => {
    setSaving(true);
    try { await api.put('/settings/marketing', marketingConfig); toast.success(t('tenantSettings.marketingSaved')); }
    catch (error) { toast.error(t('tenantSettings.saveFailed')); } finally { setSaving(false); }
  };

  const saveAuthConfig = async () => {
    setSaving(true);
    try { await api.put('/settings/auth', authConfig); toast.success(t('tenantSettings.authSaved')); }
    catch (error) { toast.error(t('tenantSettings.saveFailed')); } finally { setSaving(false); }
  };

  const saveFeaturesConfig = async () => {
    setSaving(true);
    try { await api.put('/settings/features', featuresConfig); toast.success(t('tenantSettings.featuresSaved')); }
    catch (error) { toast.error(t('tenantSettings.saveFailed')); } finally { setSaving(false); }
  };

  const saveEinvoiceConfig = async () => {
    setSaving(true);
    try { await api.put('/settings/einvoice', einvoiceConfig); toast.success(t('tenantSettings.einvoiceSaved')); }
    catch (error) { toast.error(t('tenantSettings.saveFailed')); } finally { setSaving(false); }
  };

  const saveNotificationConfig = async () => {
    setSaving(true);
    try { await api.put('/settings/notifications', notificationConfig); toast.success(t('tenantSettings.notificationsSaved')); }
    catch (error) { toast.error(t('tenantSettings.saveFailed')); } finally { setSaving(false); }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  // ===== Tab Content =====
  const renderTabContent = () => {
    switch (activeTab) {
      case 0: return renderStoreInfo();
      case 1: return renderTemplateSelection();
      case 2: return renderShipping();
      case 3: return renderContact();
      case 4: return renderFeatures();
      case 5: return renderPayment();
      case 6: return renderMarketing();
      case 7: return renderAuth();
      case 8: return renderSeo();
      case 9: return renderDomains();
      case 10: return renderEinvoice();
      case 11: return renderNotifications();
      default: return null;
    }
  };

  // ===== Tab 0: Store =====
  const renderStoreInfo = () => (
    <Box>
      <Typography variant="h6" gutterBottom>{t('tenantSettings.storeBasicInfo')}</Typography>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <TextField fullWidth label={t('tenantSettings.storeName')} value={storeConfig.store_name}
            onChange={(e) => setStoreConfig({ ...storeConfig, store_name: e.target.value })}
            helperText={t('tenantSettings.storeNameHint')} />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField fullWidth label={t('tenantSettings.storeNameShort')} value={storeConfig.store_name_short}
            onChange={(e) => setStoreConfig({ ...storeConfig, store_name_short: e.target.value })}
            helperText={t('tenantSettings.storeNameShortHint')} />
        </Grid>
        <Grid item xs={12}>
          <TextField fullWidth label={t('tenantSettings.storeSlogan')} value={storeConfig.slogan}
            onChange={(e) => setStoreConfig({ ...storeConfig, slogan: e.target.value })}
            placeholder={t('tenantSettings.sloganPlaceholder')} />
        </Grid>
        <Grid item xs={12}>
          <TextField fullWidth multiline rows={3} label={t('tenantSettings.storeDescription')} value={storeConfig.description}
            onChange={(e) => setStoreConfig({ ...storeConfig, description: e.target.value })}
            placeholder={t('tenantSettings.descriptionPlaceholder')} />
        </Grid>

        <Grid item xs={12}><Divider sx={{ my: 1 }}><Chip label={t('tenantSettings.logoIcon')} /></Divider></Grid>

        <Grid item xs={12} md={6}>
          <Typography variant="subtitle2" gutterBottom>Logo</Typography>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
            {storeConfig.logo_url ? (
              <Box sx={{ position: 'relative' }}>
                <Box component="img" src={storeConfig.logo_url.startsWith('/') ? `http://localhost:88${storeConfig.logo_url}` : storeConfig.logo_url}
                  alt="Logo" sx={{ maxWidth: 200, maxHeight: 80, objectFit: 'contain', border: '1px solid #ddd', borderRadius: 1, p: 1 }} />
                <IconButton size="small" onClick={() => handleClearImage('logo_url')}
                  sx={{ position: 'absolute', top: -8, right: -8, bgcolor: 'error.main', color: 'white', '&:hover': { bgcolor: 'error.dark' } }}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            ) : (
              <Box sx={{ width: 200, height: 80, border: '2px dashed #ddd', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'text.secondary' }}>{t('tenantSettings.notUploaded')}</Box>
            )}
            <Box>
              <input type="file" accept="image/*" id="logo-upload" style={{ display: 'none' }}
                onChange={(e) => handleImageUpload(e, 'logo_url')} />
              <label htmlFor="logo-upload">
                <Button variant="outlined" component="span" startIcon={uploading ? <CircularProgress size={16} /> : <UploadIcon />} disabled={uploading}>{t('tenantSettings.uploadLogo')}</Button>
              </label>
              <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>{t('tenantSettings.logoSizeHint')}</Typography>
            </Box>
          </Box>
          <TextField fullWidth size="small" label={t('tenantSettings.logoUrl')} value={storeConfig.logo_url}
            onChange={(e) => setStoreConfig({ ...storeConfig, logo_url: e.target.value })} placeholder="https://..." sx={{ mt: 2 }} />
        </Grid>

        <Grid item xs={12} md={6}>
          <Typography variant="subtitle2" gutterBottom>{t('tenantSettings.favicon')}</Typography>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
            {storeConfig.favicon_url ? (
              <Box sx={{ position: 'relative' }}>
                <Box component="img" src={storeConfig.favicon_url.startsWith('/') ? `http://localhost:88${storeConfig.favicon_url}` : storeConfig.favicon_url}
                  alt="Favicon" sx={{ width: 48, height: 48, objectFit: 'contain', border: '1px solid #ddd', borderRadius: 1, p: 1 }} />
                <IconButton size="small" onClick={() => handleClearImage('favicon_url')}
                  sx={{ position: 'absolute', top: -8, right: -8, bgcolor: 'error.main', color: 'white', '&:hover': { bgcolor: 'error.dark' } }}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            ) : (
              <Box sx={{ width: 48, height: 48, border: '2px dashed #ddd', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'text.secondary', fontSize: 12 }}>{t('tenantSettings.noIcon')}</Box>
            )}
            <Box>
              <input type="file" accept="image/*" id="favicon-upload" style={{ display: 'none' }}
                onChange={(e) => handleImageUpload(e, 'favicon_url')} />
              <label htmlFor="favicon-upload">
                <Button variant="outlined" component="span" size="small" startIcon={uploading ? <CircularProgress size={16} /> : <UploadIcon />} disabled={uploading}>{t('tenantSettings.upload')}</Button>
              </label>
              <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>{t('tenantSettings.faviconSizeHint')}</Typography>
            </Box>
          </Box>
          <TextField fullWidth size="small" label={t('tenantSettings.faviconUrl')} value={storeConfig.favicon_url}
            onChange={(e) => setStoreConfig({ ...storeConfig, favicon_url: e.target.value })} placeholder="https://..." sx={{ mt: 2 }} />
        </Grid>
      </Grid>
      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="contained" startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />} onClick={handleSaveStore} disabled={saving}>{t('tenantSettings.saveStoreInfo')}</Button>
      </Box>
    </Box>
  );

  // ===== Tab 1: Theme / Template =====
  const renderTemplateSelection = () => (
    <Box>
      <Typography variant="h6" gutterBottom>{t('tenantSettings.themeTitle')}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t('tenantSettings.themeHint')}
      </Typography>
      <Grid container spacing={2}>
        {themes.map((theme) => (
          <Grid item xs={12} sm={6} md={4} key={theme.id}>
            <Card variant={themeConfig.theme_id === theme.id ? 'outlined' : 'elevation'}
              sx={{ borderColor: themeConfig.theme_id === theme.id ? theme.color : 'divider', borderWidth: themeConfig.theme_id === theme.id ? 2 : 1, position: 'relative' }}>
              {themeConfig.theme_id === theme.id && (
                <Chip icon={<CheckIcon />} label={t('tenantSettings.selected')} size="small" sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1, bgcolor: theme.color, color: 'white' }} />
              )}
              <CardActionArea onClick={() => handleSelectTheme(theme.id)}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Avatar sx={{ bgcolor: theme.color, width: 48, height: 48 }}><PaletteIcon /></Avatar>
                    <Box>
                      <Typography variant="h6">{theme.name}</Typography>
                      <Typography variant="caption" color="text.secondary">{theme.color}</Typography>
                    </Box>
                  </Box>
                  <Typography variant="body2" color="text.secondary">{theme.description}</Typography>
                  <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                    <Box sx={{ width: 40, height: 20, bgcolor: theme.color, borderRadius: 1 }} />
                    <Box sx={{ width: 40, height: 20, bgcolor: adjustColor(theme.color, 40), borderRadius: 1 }} />
                    <Box sx={{ width: 40, height: 20, bgcolor: adjustColor(theme.color, -40), borderRadius: 1 }} />
                  </Box>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Divider sx={{ my: 4 }}><Chip label={t('tenantSettings.template')} /></Divider>

      <Typography variant="h6" gutterBottom>{t('tenantSettings.storeTemplate')}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t('tenantSettings.templateHint')}
      </Typography>
      <Grid container spacing={2}>
        {STORE_TEMPLATES.map((tmpl) => (
          <Grid item xs={12} sm={6} md={4} key={tmpl.id}>
            <Card variant={selectedTemplate === tmpl.id ? 'outlined' : 'elevation'}
              sx={{ borderColor: selectedTemplate === tmpl.id ? tmpl.color : 'divider', borderWidth: selectedTemplate === tmpl.id ? 2 : 1, position: 'relative', overflow: 'hidden' }}>
              {selectedTemplate === tmpl.id && (
                <Chip icon={<CheckIcon />} label={t('tenantSettings.selected')} size="small" sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1, bgcolor: tmpl.color, color: 'white' }} />
              )}
              <CardActionArea onClick={() => handleSelectTemplate(tmpl.id)}>
                <Box sx={{ height: 140, bgcolor: tmpl.color, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', overflow: 'hidden' }}>
                  <Box component="img" src={tmpl.image} alt={tmpl.name}
                    sx={{ width: '100%', height: '100%', objectFit: 'cover', opacity: 0.7 }}
                    onError={(e) => { e.target.style.display = 'none'; }} />
                  <Typography variant="h6" sx={{ position: 'absolute', color: 'white', fontWeight: 'bold', textShadow: '0 2px 8px rgba(0,0,0,0.5)' }}>{t(tmpl.nameKey)}</Typography>
                </Box>
                <CardContent>
                  <Typography variant="subtitle1" fontWeight="bold">{t(tmpl.nameKey)}</Typography>
                  <Typography variant="body2" color="text.secondary">{t(tmpl.descKey)}</Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );

  // ===== Tab 2: Shipping =====
  const renderShipping = () => (
    <Box>
      <Typography variant="h6" gutterBottom>{t('tenantSettings.shippingTitle')}</Typography>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <TextField fullWidth type="number" label={t('tenantSettings.baseFee')} value={shippingConfig.base_fee}
            onChange={(e) => setShippingConfig({ ...shippingConfig, base_fee: parseInt(e.target.value) || 0 })}
            InputProps={{ startAdornment: '$' }} helperText={t('tenantSettings.baseFeeHint')} />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField fullWidth type="number" label={t('tenantSettings.freeShippingThreshold')} value={shippingConfig.free_shipping_threshold}
            onChange={(e) => setShippingConfig({ ...shippingConfig, free_shipping_threshold: parseInt(e.target.value) || 0 })}
            InputProps={{ startAdornment: '$' }} helperText={t('tenantSettings.freeShippingHint')} />
        </Grid>
      </Grid>
      <Alert severity="success" sx={{ mt: 3 }}>
        {t('tenantSettings.shippingSummary', { threshold: shippingConfig.free_shipping_threshold, fee: shippingConfig.base_fee })}
      </Alert>
      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="contained" startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />} onClick={handleSaveShipping} disabled={saving}>{t('tenantSettings.saveShipping')}</Button>
      </Box>
    </Box>
  );

  // ===== Tab 3: Contact =====
  const renderContact = () => (
    <Box>
      <Typography variant="h6" gutterBottom>{t('tenantSettings.contactTitle')}</Typography>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <TextField fullWidth label={t('tenantSettings.contactPhone')} value={contactConfig.contact_phone}
            onChange={(e) => setContactConfig({ ...contactConfig, contact_phone: e.target.value })} />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField fullWidth label={t('tenantSettings.contactEmail')} value={contactConfig.contact_email}
            onChange={(e) => setContactConfig({ ...contactConfig, contact_email: e.target.value })} />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField fullWidth label="LINE ID" value={contactConfig.line_id}
            onChange={(e) => setContactConfig({ ...contactConfig, line_id: e.target.value })} placeholder="@xxx" />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField fullWidth label={t('tenantSettings.serviceHours')} value={contactConfig.service_hours}
            onChange={(e) => setContactConfig({ ...contactConfig, service_hours: e.target.value })} placeholder={t('tenantSettings.serviceHoursPlaceholder')} />
        </Grid>
        <Grid item xs={12}>
          <TextField fullWidth label={t('tenantSettings.address')} value={contactConfig.contact_address}
            onChange={(e) => setContactConfig({ ...contactConfig, contact_address: e.target.value })} />
        </Grid>
        <Grid item xs={12}><Divider sx={{ my: 1 }}><Chip label={t('tenantSettings.socialLinks')} /></Divider></Grid>
        <Grid item xs={12} md={4}>
          <TextField fullWidth label={t('tenantSettings.facebookLink')} value={contactConfig.facebook_url}
            onChange={(e) => setContactConfig({ ...contactConfig, facebook_url: e.target.value })} placeholder="https://facebook.com/..." />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField fullWidth label="Instagram" value={contactConfig.instagram_url}
            onChange={(e) => setContactConfig({ ...contactConfig, instagram_url: e.target.value })} placeholder="https://instagram.com/..." />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField fullWidth label={t('tenantSettings.lineLink')} value={contactConfig.line_url}
            onChange={(e) => setContactConfig({ ...contactConfig, line_url: e.target.value })} placeholder="https://line.me/R/ti/p/..." />
        </Grid>
      </Grid>
      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="contained" startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />} onClick={handleSaveContact} disabled={saving}>{t('tenantSettings.saveContact')}</Button>
      </Box>
    </Box>
  );

  // ===== Tab 4: Features =====
  const renderFeatures = () => (
    <Box>
      <Typography variant="h6" gutterBottom>{t('tenantSettings.featuresTitle')}</Typography>
      <Alert severity="info" sx={{ mb: 2 }}>{t('tenantSettings.featuresHint')}</Alert>
      <Grid container spacing={3}>
        {[1, 2, 3, 4].map((num) => {
          const IconComponent = iconMap[featuresConfig[`feature${num}_icon`]] || LocalOfferIcon;
          return (
            <Grid item xs={12} md={6} key={num}>
              <Card variant="outlined">
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <IconComponent color="primary" sx={{ fontSize: 40 }} />
                    <Typography variant="subtitle1" fontWeight="bold">{t('tenantSettings.sellingPoint')} {num}</Typography>
                  </Box>
                  <TextField fullWidth size="small" label={t('tenantSettings.iconName')} value={featuresConfig[`feature${num}_icon`]}
                    onChange={(e) => setFeaturesConfig(prev => ({ ...prev, [`feature${num}_icon`]: e.target.value }))}
                    helperText="LocalOffer, LocalShipping, VerifiedUser, Support" sx={{ mb: 2 }} />
                  <TextField fullWidth size="small" label={t('tenantSettings.fieldTitle')} value={featuresConfig[`feature${num}_title`]}
                    onChange={(e) => setFeaturesConfig(prev => ({ ...prev, [`feature${num}_title`]: e.target.value }))} sx={{ mb: 2 }} />
                  <TextField fullWidth size="small" label={t('tenantSettings.fieldDescription')} value={featuresConfig[`feature${num}_desc`]}
                    onChange={(e) => setFeaturesConfig(prev => ({ ...prev, [`feature${num}_desc`]: e.target.value }))} />
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>
      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="contained" startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />} onClick={saveFeaturesConfig} disabled={saving}>{t('tenantSettings.saveFeatures')}</Button>
      </Box>
    </Box>
  );

  // ===== Tab 5: Payments =====
  const renderPayment = () => (
    <Box>
      <Typography variant="h6" gutterBottom>{t('tenantSettings.paymentTitle')}</Typography>

      <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 2, mb: 1 }}>{t('tenantSettings.paymentMethodsSortTitle')}</Typography>
      <Alert severity="info" sx={{ mb: 2 }}>{t('tenantSettings.paymentMethodsHint')}</Alert>
      <TableContainer component={Paper} variant="outlined" sx={{ mb: 4 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell width={80}>{t('tenantSettings.sort')}</TableCell>
              <TableCell>{t('tenantSettings.paymentMethod')}</TableCell>
              <TableCell>{t('tenantSettings.description')}</TableCell>
              <TableCell width={80} align="center">{t('tenantSettings.status')}</TableCell>
              <TableCell width={80} align="center">{t('tenantSettings.enable')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paymentMethods.map((method, index) => (
              <TableRow key={method.id}>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <IconButton size="small" onClick={() => movePaymentMethod(method.id, -1)} disabled={index === 0}><UpIcon fontSize="small" /></IconButton>
                    <IconButton size="small" onClick={() => movePaymentMethod(method.id, 1)} disabled={index === paymentMethods.length - 1}><DownIcon fontSize="small" /></IconButton>
                  </Box>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {method.logo_url ? <img src={method.logo_url} alt={method.name} style={{ height: 20 }} /> : method.icon}
                    <Typography fontWeight="bold">{method.name}</Typography>
                    <Typography variant="caption" color="text.secondary">({method.code})</Typography>
                  </Box>
                </TableCell>
                <TableCell><Typography variant="body2" color="text.secondary">{method.description}</Typography></TableCell>
                <TableCell align="center"><Chip label={method.is_active ? t('tenantSettings.enabled') : t('tenantSettings.disabled')} color={method.is_active ? 'success' : 'default'} size="small" /></TableCell>
                <TableCell align="center"><Switch checked={!!method.is_active} onChange={(e) => togglePaymentMethod(method.id, e.target.checked)} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Bank Account for manual ATM */}
      <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>ATM 轉帳收款帳戶</Typography>
      <Alert severity="info" sx={{ mb: 2 }}>客人選擇 ATM 轉帳時，系統會顯示以下帳戶資訊供客人匯款（需人工對帳）。若已設定 SmilePay，則會自動產生虛擬帳號。</Alert>
      <Divider sx={{ mb: 2 }} />
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}><TextField fullWidth label="銀行名稱" placeholder="例：國泰世華" value={bankConfig.bank_name} onChange={(e) => setBankConfig(prev => ({ ...prev, bank_name: e.target.value }))} /></Grid>
        <Grid item xs={12} md={6}><TextField fullWidth label="銀行代碼" placeholder="例：013" value={bankConfig.bank_code} onChange={(e) => setBankConfig(prev => ({ ...prev, bank_code: e.target.value }))} /></Grid>
        <Grid item xs={12} md={6}><TextField fullWidth label="帳號" placeholder="銀行帳號" value={bankConfig.bank_account} onChange={(e) => setBankConfig(prev => ({ ...prev, bank_account: e.target.value }))} /></Grid>
        <Grid item xs={12} md={6}><TextField fullWidth label="戶名" placeholder="帳戶持有人姓名" value={bankConfig.bank_holder} onChange={(e) => setBankConfig(prev => ({ ...prev, bank_holder: e.target.value }))} /></Grid>
        <Grid item xs={12}><Button variant="contained" startIcon={<SaveIcon />} onClick={saveBankConfig} disabled={saving}>儲存收款帳戶</Button></Grid>
      </Grid>

      {/* SmilePay */}
      <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 4, mb: 1 }}>{t('tenantSettings.smilepayPayment')}</Typography>
      <Alert severity="info" sx={{ mb: 2 }}>設定 SmilePay 後，ATM 轉帳將自動產生虛擬帳號（免人工對帳），並可啟用信用卡、超商代碼等付款方式。</Alert>
      <Divider sx={{ mb: 2 }} />
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}><TextField fullWidth label={t('tenantSettings.merchantCode')} value={smilepayConfig.dcvc} onChange={(e) => setSmilepayConfig(prev => ({ ...prev, dcvc: e.target.value }))} /></Grid>
        <Grid item xs={12} md={6}><TextField fullWidth label={t('tenantSettings.verifyCode')} value={smilepayConfig.rvg2c_key} onChange={(e) => setSmilepayConfig(prev => ({ ...prev, rvg2c_key: e.target.value }))} /></Grid>
        <Grid item xs={12}><TextField fullWidth label={t('tenantSettings.rotUrl')} value={smilepayConfig.rot_url} onChange={(e) => setSmilepayConfig(prev => ({ ...prev, rot_url: e.target.value }))} /></Grid>
        <Grid item xs={12}><TextField fullWidth label={t('tenantSettings.payEndUrl')} value={smilepayConfig.pay_end_url} onChange={(e) => setSmilepayConfig(prev => ({ ...prev, pay_end_url: e.target.value }))} /></Grid>
        <Grid item xs={12}><FormControlLabel control={<Switch checked={smilepayConfig.test_mode} onChange={(e) => setSmilepayConfig(prev => ({ ...prev, test_mode: e.target.checked }))} />} label={t('tenantSettings.testMode')} /></Grid>
        <Grid item xs={12}><Button variant="contained" startIcon={<SaveIcon />} onClick={saveSmilepayConfig} disabled={saving}>{t('tenantSettings.saveSmilepaySetting')}</Button></Grid>
      </Grid>

      {/* NCCC */}
      <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 4, mb: 1 }}>{t('tenantSettings.ncccTitle')}</Typography>
      <Divider sx={{ mb: 2 }} />
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}><TextField fullWidth label={t('tenantSettings.ncccMerchantId')} value={ncccConfig.merchant_id} onChange={(e) => setNcccConfig(prev => ({ ...prev, merchant_id: e.target.value }))} /></Grid>
        <Grid item xs={12} md={6}><TextField fullWidth label="Terminal ID" value={ncccConfig.terminal_id} onChange={(e) => setNcccConfig(prev => ({ ...prev, terminal_id: e.target.value }))} /></Grid>
        <Grid item xs={12} md={6}><TextField fullWidth label="HashKey" value={ncccConfig.hash_key} onChange={(e) => setNcccConfig(prev => ({ ...prev, hash_key: e.target.value }))} type="password" /></Grid>
        <Grid item xs={12} md={6}><TextField fullWidth label="HashIV" value={ncccConfig.hash_iv} onChange={(e) => setNcccConfig(prev => ({ ...prev, hash_iv: e.target.value }))} type="password" /></Grid>
        <Grid item xs={12}><FormControlLabel control={<Switch checked={ncccConfig.test_mode} onChange={(e) => setNcccConfig(prev => ({ ...prev, test_mode: e.target.checked }))} />} label={t('tenantSettings.testMode')} /></Grid>
        <Grid item xs={12}><Button variant="contained" startIcon={<SaveIcon />} onClick={saveNcccConfig} disabled={saving}>{t('tenantSettings.saveNcccSetting')}</Button></Grid>
      </Grid>

      {/* LINE Pay */}
      <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 4, mb: 1 }}>LINE Pay</Typography>
      <Divider sx={{ mb: 2 }} />
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}><TextField fullWidth label="Channel ID" value={linepayConfig.channel_id} onChange={(e) => setLinepayConfig(prev => ({ ...prev, channel_id: e.target.value }))} /></Grid>
        <Grid item xs={12} md={6}><TextField fullWidth label="Channel Secret" value={linepayConfig.channel_secret} onChange={(e) => setLinepayConfig(prev => ({ ...prev, channel_secret: e.target.value }))} type="password" /></Grid>
        <Grid item xs={12}><FormControlLabel control={<Switch checked={linepayConfig.test_mode} onChange={(e) => setLinepayConfig(prev => ({ ...prev, test_mode: e.target.checked }))} />} label={t('tenantSettings.testModeSandbox')} /></Grid>
        <Grid item xs={12}><Button variant="contained" startIcon={<SaveIcon />} onClick={saveLinepayConfig} disabled={saving}>{t('tenantSettings.saveLinepaySetting')}</Button></Grid>
      </Grid>
    </Box>
  );

  // ===== Tab 6: Marketing =====
  const renderMarketing = () => (
    <Box>
      <Typography variant="h6" gutterBottom>{t('tenantSettings.marketingTitle')}</Typography>
      <Alert severity="info" sx={{ mb: 2 }}>
        {t('tenantSettings.marketingHint')}
      </Alert>
      <Grid container spacing={2}>
        <Grid item xs={12}><Typography variant="subtitle1" fontWeight="bold">{t('tenantSettings.googleTracking')}</Typography><Divider sx={{ mb: 1 }} /></Grid>
        <Grid item xs={12} md={6}><TextField fullWidth label="GTM ID" value={marketingConfig.gtm_id} onChange={(e) => setMarketingConfig(prev => ({ ...prev, gtm_id: e.target.value }))} placeholder="GTM-XXXXXXX" /></Grid>
        <Grid item xs={12} md={6}><TextField fullWidth label="GA4 ID" value={marketingConfig.ga4_id} onChange={(e) => setMarketingConfig(prev => ({ ...prev, ga4_id: e.target.value }))} placeholder="G-XXXXXXXXXX" /></Grid>
        <Grid item xs={12} md={6}><TextField fullWidth label="Google Ads ID" value={marketingConfig.google_ads_id} onChange={(e) => setMarketingConfig(prev => ({ ...prev, google_ads_id: e.target.value }))} placeholder="AW-XXXXXXXXXX" /></Grid>
        <Grid item xs={12} md={6}><TextField fullWidth label={t('tenantSettings.googleAdsLabel')} value={marketingConfig.google_ads_label} onChange={(e) => setMarketingConfig(prev => ({ ...prev, google_ads_label: e.target.value }))} placeholder="e.g. abcdEFGhijkLMn" /></Grid>

        <Grid item xs={12} sx={{ mt: 2 }}><Typography variant="subtitle1" fontWeight="bold">{t('tenantSettings.facebookTracking')}</Typography><Divider sx={{ mb: 1 }} /></Grid>
        <Grid item xs={12} md={6}><TextField fullWidth label="Facebook Pixel ID" value={marketingConfig.fb_pixel_id} onChange={(e) => setMarketingConfig(prev => ({ ...prev, fb_pixel_id: e.target.value }))} placeholder="1234567890123456" /></Grid>
        <Grid item xs={12} md={6}><TextField fullWidth label="Facebook Access Token" value={marketingConfig.fb_access_token} onChange={(e) => setMarketingConfig(prev => ({ ...prev, fb_access_token: e.target.value }))} type="password" helperText="Conversions API token (optional)." /></Grid>

        <Grid item xs={12} sx={{ mt: 2 }}><Typography variant="subtitle1" fontWeight="bold">{t('tenantSettings.tiktokTracking')}</Typography><Divider sx={{ mb: 1 }} /></Grid>
        <Grid item xs={12} md={6}><TextField fullWidth label="TikTok Pixel ID" value={marketingConfig.tiktok_pixel_id} onChange={(e) => setMarketingConfig(prev => ({ ...prev, tiktok_pixel_id: e.target.value }))} /></Grid>
        <Grid item xs={12} md={6}><TextField fullWidth label="TikTok Access Token" value={marketingConfig.tiktok_access_token} onChange={(e) => setMarketingConfig(prev => ({ ...prev, tiktok_access_token: e.target.value }))} type="password" /></Grid>
        <Grid item xs={12}><FormControlLabel control={<Switch checked={marketingConfig.tiktok_test_mode} onChange={(e) => setMarketingConfig(prev => ({ ...prev, tiktok_test_mode: e.target.checked }))} />} label={t('tenantSettings.tiktokTestMode')} /></Grid>
      </Grid>
      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="contained" startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />} onClick={saveMarketingConfig} disabled={saving}>{t('tenantSettings.saveMarketing')}</Button>
      </Box>
    </Box>
  );

  // ===== Tab 7: Auth =====
  const renderAuth = () => (
    <Box>
      <Typography variant="h6" gutterBottom>{t('tenantSettings.authTitle')}</Typography>
      <Alert severity="info" sx={{ mb: 2 }}>{t('tenantSettings.authHint')}</Alert>
      <Grid container spacing={2}>
        <Grid item xs={12}><Typography variant="subtitle1" fontWeight="bold">{t('tenantSettings.phoneLogin')}</Typography><Divider sx={{ mb: 1 }} /></Grid>
        <Grid item xs={12}><FormControlLabel control={<Switch checked={authConfig.phone_enabled} onChange={(e) => setAuthConfig(prev => ({ ...prev, phone_enabled: e.target.checked }))} />} label={t('tenantSettings.enablePhoneLogin')} /></Grid>

        <Grid item xs={12} sx={{ mt: 2 }}><Typography variant="subtitle1" fontWeight="bold">Google OAuth</Typography><Divider sx={{ mb: 1 }} /></Grid>
        <Grid item xs={12}><FormControlLabel control={<Switch checked={authConfig.google_enabled} onChange={(e) => setAuthConfig(prev => ({ ...prev, google_enabled: e.target.checked }))} />} label={t('tenantSettings.enableGoogleLogin')} /></Grid>
        <Grid item xs={12} md={6}><TextField fullWidth label="Google Client ID" value={authConfig.google_client_id} onChange={(e) => setAuthConfig(prev => ({ ...prev, google_client_id: e.target.value }))} disabled={!authConfig.google_enabled} /></Grid>
        <Grid item xs={12} md={6}><TextField fullWidth label="Google Client Secret" value={authConfig.google_client_secret} onChange={(e) => setAuthConfig(prev => ({ ...prev, google_client_secret: e.target.value }))} type="password" disabled={!authConfig.google_enabled} /></Grid>

        <Grid item xs={12} sx={{ mt: 2 }}><Typography variant="subtitle1" fontWeight="bold">LINE Login</Typography><Divider sx={{ mb: 1 }} /></Grid>
        <Grid item xs={12}><FormControlLabel control={<Switch checked={authConfig.line_enabled} onChange={(e) => setAuthConfig(prev => ({ ...prev, line_enabled: e.target.checked }))} />} label={t('tenantSettings.enableLineLogin')} /></Grid>
        <Grid item xs={12} md={6}><TextField fullWidth label="LINE Channel ID" value={authConfig.line_channel_id} onChange={(e) => setAuthConfig(prev => ({ ...prev, line_channel_id: e.target.value }))} disabled={!authConfig.line_enabled} /></Grid>
        <Grid item xs={12} md={6}><TextField fullWidth label="LINE Channel Secret" value={authConfig.line_channel_secret} onChange={(e) => setAuthConfig(prev => ({ ...prev, line_channel_secret: e.target.value }))} type="password" disabled={!authConfig.line_enabled} /></Grid>
        <Grid item xs={12}><TextField fullWidth label="LINE Callback URL" value={authConfig.line_callback_url} onChange={(e) => setAuthConfig(prev => ({ ...prev, line_callback_url: e.target.value }))} disabled={!authConfig.line_enabled} /></Grid>
      </Grid>
      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="contained" startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />} onClick={saveAuthConfig} disabled={saving}>{t('tenantSettings.saveAuth')}</Button>
      </Box>
    </Box>
  );

  // ===== Tab 8: SEO =====
  const renderSeo = () => (
    <Box>
      <Typography variant="h6" gutterBottom>{t('tenantSettings.seoSettingsTitle')}</Typography>
      <Alert severity="info" sx={{ mb: 3 }}>
        {t('tenantSettings.seoHint')}
      </Alert>
      <Grid container spacing={3}>
        <Grid item xs={12}><TextField fullWidth label="Site Title" value={seoConfig.site_title} onChange={(e) => setSeoConfig({ ...seoConfig, site_title: e.target.value })} helperText="Used in browser title and SEO." /></Grid>
        <Grid item xs={12}><TextField fullWidth multiline rows={3} label="Meta Description" value={seoConfig.meta_description} onChange={(e) => setSeoConfig({ ...seoConfig, meta_description: e.target.value })} helperText={t('tenantSettings.metaDescriptionHint')} /></Grid>
        <Grid item xs={12}><TextField fullWidth label="Meta Keywords" value={seoConfig.meta_keywords} onChange={(e) => setSeoConfig({ ...seoConfig, meta_keywords: e.target.value })} placeholder={t('tenantSettings.keywordsPlaceholder')} helperText={t('tenantSettings.keywordsHint')} /></Grid>
        <Grid item xs={12}><Divider sx={{ my: 1 }}><Chip label={t('tenantSettings.webmasterVerification')} /></Divider></Grid>
        <Grid item xs={12} md={6}><TextField fullWidth label="Google Search Console Verification" value={seoConfig.google_verification} onChange={(e) => setSeoConfig({ ...seoConfig, google_verification: e.target.value })} /></Grid>
        <Grid item xs={12} md={6}><TextField fullWidth label="Bing Webmaster Verification" value={seoConfig.bing_verification} onChange={(e) => setSeoConfig({ ...seoConfig, bing_verification: e.target.value })} /></Grid>

        {/* AI SEO 設定 */}
        <Grid item xs={12}><Divider sx={{ my: 1 }}><Chip label="AI SEO" /></Divider></Grid>
        <Grid item xs={12}>
          <Alert severity="info" sx={{ mb: 1 }}>
            AI SEO 功能可讓您的商店被 ChatGPT、Perplexity、Google AI Overview 等 AI 搜尋引擎正確索引與引用。
            啟用後會自動生成 llms.txt 檔案，並在 robots.txt 中加入 AI 爬蟲規則。
          </Alert>
        </Grid>
        <Grid item xs={12} md={6}>
          <FormControlLabel
            control={<Switch checked={seoConfig.ai_seo_enabled} onChange={(e) => setSeoConfig({ ...seoConfig, ai_seo_enabled: e.target.checked })} />}
            label="啟用 AI SEO（llms.txt + 進階 robots.txt）"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <FormControlLabel
            control={<Switch checked={seoConfig.block_ai_training} onChange={(e) => setSeoConfig({ ...seoConfig, block_ai_training: e.target.checked })} />}
            label="封鎖 AI 訓練爬蟲（GPTBot、CCBot 等）"
          />
          <Typography variant="caption" color="text.secondary" display="block">
            建議開啟：允許 AI 搜尋引擎引用您的商店，但封鎖只抓資料不引用來源的訓練爬蟲。
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            multiline
            rows={6}
            label="自訂 llms.txt 內容（可選）"
            value={seoConfig.llms_txt_custom}
            onChange={(e) => setSeoConfig({ ...seoConfig, llms_txt_custom: e.target.value })}
            placeholder="留空則由系統自動根據商店資料生成。可使用 Markdown 格式。"
            helperText="llms.txt 是專為 AI 爬蟲設計的商店摘要檔案，類似 robots.txt 但提供商店結構化資訊。留空則自動生成。"
          />
        </Grid>
      </Grid>
      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="contained" startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />} onClick={handleSaveSeo} disabled={saving}>{t('tenantSettings.saveSeo')}</Button>
      </Box>
    </Box>
  );

  // ===== Tab 9: Domains =====
  const renderDomains = () => (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">{t('tenantSettings.domainsTitle')}</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setAddDomainOpen(true)}>{t('tenantSettings.addDomain')}</Button>
      </Box>
      <Alert severity="info" sx={{ mb: 3 }}>{t('tenantSettings.domainsCnameHint')}</Alert>

      {domainsLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
      ) : domains.length === 0 ? (
        <Alert severity="warning">{t('tenantSettings.noDomainsYet')}</Alert>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('tenantSettings.domain')}</TableCell>
                <TableCell>{t('tenantSettings.type')}</TableCell>
                <TableCell align="center">{t('tenantSettings.dnsVerification')}</TableCell>
                <TableCell align="center">{t('tenantSettings.primaryDomain')}</TableCell>
                <TableCell align="center">{t('tenantSettings.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {domains.map((d) => (
                <TableRow key={d.id}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <DnsIcon color="action" fontSize="small" />
                      <Typography variant="body2" fontWeight="bold">{d.domain}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell><Chip label={d.type === 'subdomain' ? t('tenantSettings.subdomain') : t('tenantSettings.customDomain')} size="small" color={d.type === 'subdomain' ? 'default' : 'primary'} variant="outlined" /></TableCell>
                  <TableCell align="center">
                    {d.dns_verified ? <Chip icon={<VerifiedIcon />} label={t('tenantSettings.verified')} size="small" color="success" /> : <Chip label={t('tenantSettings.pendingVerification')} size="small" color="warning" />}
                  </TableCell>
                  <TableCell align="center">
                    {d.is_primary ? <StarIcon color="warning" /> : <Tooltip title={t('tenantSettings.setAsPrimary')}><IconButton size="small" onClick={() => handleSetPrimary(d.id)}><StarBorderIcon /></IconButton></Tooltip>}
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                      {d.type !== 'subdomain' && !d.dns_verified && (
                        <Tooltip title={t('tenantSettings.verifyDns')}><IconButton size="small" color="primary" onClick={() => handleVerifyDomain(d.id)} disabled={verifyingId === d.id}>
                          {verifyingId === d.id ? <CircularProgress size={18} /> : <VerifiedIcon />}
                        </IconButton></Tooltip>
                      )}
                      {d.type !== 'subdomain' && (
                        <Tooltip title={t('tenantSettings.deleteDomain')}><IconButton size="small" color="error" onClick={() => handleDeleteDomain(d.id, d.domain)}><DeleteIcon /></IconButton></Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Paper variant="outlined" sx={{ p: 2, mt: 3, bgcolor: 'grey.50' }}>
        <Typography variant="subtitle2" gutterBottom>{t('tenantSettings.dnsCnameTitle')}</Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          {t('tenantSettings.dnsCnameDesc')}
        </Typography>
        <Box sx={{ bgcolor: 'grey.100', p: 1.5, borderRadius: 1, fontFamily: 'monospace', fontSize: 14, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span>CNAME &nbsp; your-domain.com &nbsp; -&gt;&nbsp; store.urstore.tw</span>
          <IconButton size="small" onClick={() => copyToClipboard('store.urstore.tw')}><CopyIcon fontSize="small" /></IconButton>
        </Box>
      </Paper>

      <Dialog open={addDomainOpen} onClose={() => setAddDomainOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('tenantSettings.addCustomDomain')}</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>{t('tenantSettings.addDomainHint')}</Alert>
          <TextField fullWidth label={t('tenantSettings.customDomain')} value={newDomain} onChange={(e) => setNewDomain(e.target.value.toLowerCase())}
            placeholder={t('tenantSettings.domainPlaceholder')}
            helperText={t('tenantSettings.domainHelperText')}
            autoFocus
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDomainOpen(false)}>{t('tenantSettings.cancel')}</Button>
          <Button variant="contained" onClick={handleAddDomain} disabled={addingDomain || !newDomain.trim()}
            startIcon={addingDomain ? <CircularProgress size={18} /> : <AddIcon />}>{t('tenantSettings.add')}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );

  // ===== Tab 10: 電子發票設定 =====
  const renderEinvoice = () => (
    <Box>
      <Typography variant="h6" gutterBottom>{t('tenantSettings.einvoiceTitle')}</Typography>
      <Alert severity="info" sx={{ mb: 2 }}>
        {t('tenantSettings.einvoiceHint')}
      </Alert>
      <Grid container spacing={2}>
        <Grid item xs={12}><Typography variant="subtitle1" fontWeight="bold">{t('tenantSettings.apiSettings')}</Typography><Divider sx={{ mb: 1 }} /></Grid>
        <Grid item xs={12} md={6}><TextField fullWidth label={t('tenantSettings.einvoiceMerchantCode')} value={einvoiceConfig.grvc} onChange={(e) => setEinvoiceConfig(prev => ({ ...prev, grvc: e.target.value }))} /></Grid>
        <Grid item xs={12} md={6}><TextField fullWidth label={t('tenantSettings.einvoiceVerifyKey')} value={einvoiceConfig.verify_key} onChange={(e) => setEinvoiceConfig(prev => ({ ...prev, verify_key: e.target.value }))} type="password" /></Grid>

        <Grid item xs={12} sx={{ mt: 2 }}><Typography variant="subtitle1" fontWeight="bold">{t('tenantSettings.invoiceSettings')}</Typography><Divider sx={{ mb: 1 }} /></Grid>
        <Grid item xs={12} md={6}>
          <TextField fullWidth select label={t('tenantSettings.invoiceType')} value={einvoiceConfig.intype} onChange={(e) => setEinvoiceConfig(prev => ({ ...prev, intype: e.target.value }))} SelectProps={{ native: true }}>
            <option value="07">{t('tenantSettings.invoiceType07')}</option><option value="08">{t('tenantSettings.invoiceType08')}</option>
          </TextField>
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField fullWidth select label={t('tenantSettings.taxType')} value={einvoiceConfig.tax_type} onChange={(e) => setEinvoiceConfig(prev => ({ ...prev, tax_type: e.target.value }))} SelectProps={{ native: true }}>
            <option value="1">{t('tenantSettings.taxTypeTaxable')}</option><option value="2">{t('tenantSettings.taxTypeZero')}</option><option value="3">{t('tenantSettings.taxTypeExempt')}</option>
          </TextField>
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField fullWidth select label={t('tenantSettings.defaultCarrier')} value={einvoiceConfig.default_carrier} onChange={(e) => setEinvoiceConfig(prev => ({ ...prev, default_carrier: e.target.value }))} SelectProps={{ native: true }}>
            <option value="EJ0113">EJ0113</option><option value="3J0002">3J0002</option><option value="CQ0001">CQ0001</option>
          </TextField>
        </Grid>

        <Grid item xs={12} sx={{ mt: 2 }}><Typography variant="subtitle1" fontWeight="bold">{t('tenantSettings.issuingOptions')}</Typography><Divider sx={{ mb: 1 }} /></Grid>
        <Grid item xs={12} md={4}><FormControlLabel control={<Switch checked={einvoiceConfig.test_mode} onChange={(e) => setEinvoiceConfig(prev => ({ ...prev, test_mode: e.target.checked }))} />} label={t('tenantSettings.testMode')} /></Grid>
        <Grid item xs={12} md={4}><FormControlLabel control={<Switch checked={einvoiceConfig.print_enabled} onChange={(e) => setEinvoiceConfig(prev => ({ ...prev, print_enabled: e.target.checked }))} />} label={t('tenantSettings.allowPrint')} /></Grid>
        <Grid item xs={12} md={4}><FormControlLabel control={<Switch checked={einvoiceConfig.auto_issue} onChange={(e) => setEinvoiceConfig(prev => ({ ...prev, auto_issue: e.target.checked }))} />} label={t('tenantSettings.autoIssue')} /></Grid>
      </Grid>
      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="contained" startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />} onClick={saveEinvoiceConfig} disabled={saving}>{t('tenantSettings.saveEinvoice')}</Button>
      </Box>
    </Box>
  );

  // ===== Tab 11: 通知設定 =====
  const renderNotifications = () => (
    <Box>
      <Typography variant="h6" gutterBottom>{t('tenantSettings.notificationsTitle')}</Typography>
      <Alert severity="info" sx={{ mb: 2 }}>{t('tenantSettings.notificationsHint')}</Alert>
      <Grid container spacing={2}>
        <Grid item xs={12}><Typography variant="subtitle1" fontWeight="bold">SMS</Typography><Divider sx={{ mb: 1 }} /></Grid>
        <Grid item xs={12} md={6}><TextField fullWidth label={t('tenantSettings.smsUsername')} value={notificationConfig.sms?.username || ''} onChange={(e) => setNotificationConfig(prev => ({ ...prev, sms: { ...prev.sms, username: e.target.value } }))} /></Grid>
        <Grid item xs={12} md={6}><TextField fullWidth label={t('tenantSettings.smsPassword')} value={notificationConfig.sms?.password || ''} onChange={(e) => setNotificationConfig(prev => ({ ...prev, sms: { ...prev.sms, password: e.target.value } }))} type="password" /></Grid>

        <Grid item xs={12} sx={{ mt: 2 }}><Typography variant="subtitle1" fontWeight="bold">Email (AWS SES)</Typography><Divider sx={{ mb: 1 }} /></Grid>
        <Grid item xs={12} md={6}><TextField fullWidth label={t('tenantSettings.senderEmail')} value={notificationConfig.email?.from_email || ''} onChange={(e) => setNotificationConfig(prev => ({ ...prev, email: { ...prev.email, from_email: e.target.value } }))} /></Grid>
        <Grid item xs={12} md={6}><TextField fullWidth label={t('tenantSettings.senderName')} value={notificationConfig.email?.from_name || ''} onChange={(e) => setNotificationConfig(prev => ({ ...prev, email: { ...prev.email, from_name: e.target.value } }))} /></Grid>

        <Grid item xs={12} sx={{ mt: 2 }}><Typography variant="subtitle1" fontWeight="bold">LINE Notify</Typography><Divider sx={{ mb: 1 }} /></Grid>
        <Grid item xs={12}><TextField fullWidth label="LINE Notify Token" value={notificationConfig.line?.notify_token || ''} onChange={(e) => setNotificationConfig(prev => ({ ...prev, line: { ...prev.line, notify_token: e.target.value } }))} type="password" /></Grid>
      </Grid>
      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="contained" startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />} onClick={saveNotificationConfig} disabled={saving}>{t('tenantSettings.saveNotifications')}</Button>
      </Box>
    </Box>
  );

  // ===== Page =====
  return (
    <Box sx={{ p: { xs: 1, md: 3 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight="bold">{t('tenantSettings.pageTitle')}</Typography>
        <Button startIcon={<RefreshIcon />} onClick={loadAllSettings}>{t('tenantSettings.reload')}</Button>
      </Box>

      <Box sx={{ display: 'flex', gap: 3, flexDirection: isMobile ? 'column' : 'row' }}>
        {/* Tab menu */}
        <Paper sx={{ minWidth: isMobile ? 'auto' : 220, flexShrink: 0 }}>
          {!isMobile && (
            <Box sx={{ px: 2, pt: 1 }}>
              <Divider sx={{ my: 0.5 }}>
                <Chip label={t('tenantSettings.settingsMenu')} size="small" />
              </Divider>
            </Box>
          )}
          <Tabs
            orientation={isMobile ? 'horizontal' : 'vertical'}
            variant="scrollable"
            scrollButtons="auto"
            value={activeTab}
            onChange={(e, v) => {
              setActiveTab(v);
              if (v === 9) loadDomains();
            }}
            sx={{
              ...(isMobile ? {} : {
                '& .MuiTab-root': {
                  alignItems: 'flex-start',
                  textAlign: 'left',
                  minHeight: 48,
                  px: 2,
                },
              })
            }}
          >
            {TAB_KEYS.map((key, index) => (
              <Tab
                key={index}
                icon={TAB_ICONS[index]}
                iconPosition="start"
                label={t(key)}
                sx={{ minHeight: 48 }}
              />
            ))}
          </Tabs>
        </Paper>

        {/* Tab content */}
        <Paper sx={{ flex: 1, p: 3, minHeight: 400 }}>
          {renderTabContent()}
        </Paper>
      </Box>
    </Box>
  );
};

// Utilities
function adjustColor(color, amount) {
  const hex = color.replace('#', '');
  const num = parseInt(hex, 16);
  let r = (num >> 16) + amount;
  let g = ((num >> 8) & 0x00FF) + amount;
  let b = (num & 0x0000FF) + amount;
  r = Math.max(0, Math.min(255, r));
  g = Math.max(0, Math.min(255, g));
  b = Math.max(0, Math.min(255, b));
  return '#' + ((r << 16) | (g << 8) | b).toString(16).padStart(6, '0');
}

export default TenantSettings;


